import {
  formatMaxDrawdownPct,
  formatMetricMoney,
  formatMetricPct,
  formatProfitFactor,
  formatSharpe,
  formatTradeRange,
  formatWinsLosses,
} from '@/ui/lib/quantFormat'
import type { Run, RunDetail, RunMetrics } from './types'

/** Spec 038 workstream D (echo-core 0.64.0). App-side because these take the app `Run` type. */

/**
 * True when `metrics` is a non-null object. The key is always present, so
 * this decides whether the metrics strip renders.
 */
export function hasMetrics(run: Run): boolean {
  const m = run.metrics
  return m !== null && m !== undefined && typeof m === 'object' && !Array.isArray(m)
}

/** True when `equity_curve` has at least two finite `[time, value]` points. */
export function hasStoredEquity(run: RunDetail): boolean {
  const c = run.equity_curve
  if (!Array.isArray(c) || c.length < 2) return false
  return c.every(
    (p) => Array.isArray(p) && p.length >= 2 && typeof p[0] === 'number' && typeof p[1] === 'number' && Number.isFinite(p[0]) && Number.isFinite(p[1]),
  )
}

/** One cell of the metrics strip. */
export interface MetricCell {
  key: string
  label: string
  value: string
  title?: string
  /** Fixed cell width: sm 88 px, md 104 px, wide 220 px. */
  size: 'sm' | 'md' | 'wide'
}

/**
 * The strip's cells, in display order. The starting balance is an input, not a result, so it is
 * not shown. Widths are sized for the longest realistic value (1,000,000.00 style money).
 */
/** (cells) A null or missing metrics object gives `—` everywhere. */
export function metricsCells(run: Run): MetricCell[] {
  const m: Partial<RunMetrics> = run.metrics ?? {}
  const cell = (key: string, label: string, t: { text: string; title?: string }, size: MetricCell['size']): MetricCell => ({
    key,
    label,
    value: t.text,
    ...(t.title === undefined ? {} : { title: t.title }),
    size,
  })
  return [
    cell('win_rate_pct', 'Win rate', formatMetricPct(m.win_rate_pct), 'sm'),
    cell('wins_losses', 'Wins / Losses', formatWinsLosses(m.wins, m.losses), 'md'),
    cell('profit_factor', 'Profit factor', formatProfitFactor(m.profit_factor, m.wins, m.losses), 'md'),
    cell('max_drawdown_pct', 'Max DD', formatMaxDrawdownPct(m.max_drawdown_pct), 'sm'),
    cell('max_drawdown_abs', 'Max DD (abs)', formatMetricMoney(m.max_drawdown_abs), 'md'),
    cell('sharpe', 'Sharpe', formatSharpe(m.sharpe, m.sharpe_basis), 'sm'),
    cell('avg_win', 'Avg win', formatMetricMoney(m.avg_win), 'sm'),
    cell('avg_loss', 'Avg loss', formatMetricMoney(m.avg_loss), 'sm'),
    cell('commission_total', 'Commission', formatMetricMoney(m.commission_total), 'sm'),
    cell('trade_range', 'First to last trade', formatTradeRange(m.first_trade_at, m.last_trade_at), 'wide'),
  ]
}
